import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import Helmet from 'react-helmet';
import classNames from 'classnames';

import { ReduxBridgeProvider } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { getBetsForRunnerId } from '@xanadu/ui-shared/modules/bets/selectors';

import logOnce from '@xanadu/ui-shared/utils/logOnce';
import triggerResize from '@xanadu/ui-shared/utils/triggerResize';

import async from '@xanadu/ui-shared/components/asyncComponent';
import ErrorBoundary from '@xanadu/ui-shared/components/ErrorBoundary';
import InlineBetslip from '@xanadu/ui-shared/components/InlineBetslip';
// actions
import { isMBZeroProduct } from '@xanadu/ui-shared/modules/application/selectors';
import { selectRunner } from '@xanadu/ui-shared/modules/cashout/actions';
import { getCashout } from '@xanadu/ui-shared/modules/cashout/selectors';
import { changeInlineBetslipID } from '@xanadu/ui-shared/modules/inlineBetslip/actions';
import { getRunnerDataForMatchedBets } from '@xanadu/ui-shared/modules/inlineBetslip/selectors';
import { displayModal } from '@xanadu/ui-shared/modules/modal/actions';
import { loginRequest } from '@xanadu/ui-shared/modules/userSession/actions';
import { setGlobalBannerPortal } from '@xanadu/ui-shared/modules/application/actions';

// selectors
import { getRunnerPosition } from '@xanadu/ui-shared/modules/positions/selectors';

import NativeAppsPopup from '../NativeAppsPopup';
import Header from '../../containers/header/Header';
import RightSidebar from '../../containers/RightSidebar';
import Modal from '../../containers/Modal/Modal';
import Notification from '../../containers/Notification';
import MobileNavigation from '../../containers/navigation/MobileNavigation';
import Navigation from '../../containers/navigation/Navigation';
import MobileFooter from '../../containers/mobileFooter/MobileFooter';
import Overlay from '../../containers/overlay/Overlay';
import usePushStateHandler from '../../hooks/usePushStateHandler';
// TODO: make this async with react router 4
import RateLimits from '../../containers/RateLimits';
import Aside from '../../containers/App/Aside';
import GlobalBanner from '../../containers/GlobalBanner/GlobalBanner';
import CustomerOnboarding from '../../containers/CustomerOnboarding';


import { MOBILE_BREAKPOINT, PRODUCTS } from '../../constants';

import './App.scss';

const AsyncPageFooter = async(() =>
  import('../../containers/pageFooter/PageFooter').then(module => module.default)
);

const SUPPORTS_INTERSECTION_OBSERVER = typeof IntersectionObserver !== 'undefined';
// Gotta hold on to that SEO value it generates
const IS_PRERENDER = navigator.userAgent.toLowerCase().includes('prerender');

const FooterSpinner = (<div className="mb-app__footerSpinner">
  <img
    alt="loading"
    src="/assets/images/infinity-animated.svg"
    role="presentation"
  />
  <div>
    Loading
  </div>
</div>);

const isWww =
  window.location.host === 'www.matchbook.com' ||
  window.location.host === 'qa03.xcl.ie' ||
  window.location.host === 'qa03ext.xcl.ie'; // EXCEPTION FOR SEO TESTING PURPOSE

const App = ({
  location: { pathname },
  children,
  bannerHeight,
  navigationCollapsed,
  navigationVisible,
  betslipPinned,
  rightSidebarVisible,
  windowWidthSize,
  eventId,
  inlineBetslipID,
  setAppLoaded,
  customerOnboardingPopupVisible,
  product,
  sidebarContent = '',
  uiVersion,
}) => {
  const footerWrapper = useRef();
  const observer = useRef();
  const [showNativeAppsPopup, setShowNativeAppsPopup] = useState(false);
  const [showFooter, setShowFooter] = useState(!SUPPORTS_INTERSECTION_OBSERVER || IS_PRERENDER);
  const isExchange = product === PRODUCTS.exchange;
  const isHomepage = pathname === '/';

  const isMobile = windowWidthSize <= MOBILE_BREAKPOINT;

  const reduxBridgeData = useMemo(() => ({
    uiVersion,
  }), [uiVersion]);

  usePushStateHandler();

  const handleNativeAppsModalClose = () => {
    setShowNativeAppsPopup(false);
  };

  const handleIntersection = ([footer]) => {
    if (footer.isIntersecting) {
      // Don't need to keep observing if we've loaded the footer
      observer.current.disconnect();
      observer.current = null;
      setShowFooter(true);
    }
  };

  useEffect(() => {
    if (!isWww) {
      logOnce(`Host is ${window.location.host}`);
    }

    try {
      if (SUPPORTS_INTERSECTION_OBSERVER && footerWrapper.current) {
        const options = {
          rootMargin: '0px',
          threshold: 0.01
        };

        observer.current = new IntersectionObserver(handleIntersection, options);
        observer.current.observe(footerWrapper.current);
      }

      triggerResize();

      const loaderEl = document.querySelector('.mb-loading-page');

      if (loaderEl) {
        loaderEl.remove();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error at App mount', e);
    }

    setAppLoaded();

    return () => {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // NOTE: These global classes should be gradually phased out as the relevant component is updated.
    const classes = [
      ['hidden-betslip', betslipPinned],
      ['promoPage', pathname.startsWith('/promo/')],
      ['open-navigation', navigationVisible],
    ];

    classes.forEach(([className, enabled]) => (
      document.body.classList.toggle(className, !!enabled)
    ));
  }, [
    navigationVisible,
    pathname,
    betslipPinned,
  ]);

  const [showLabel, setShowLabel] = useState(true);

  const controlNavbar = () => {
    if (window.scrollY > 0) {
      setShowLabel(false);
    } else {
      setShowLabel(true);
    }
  };

  useEffect(() => {
    if (isMobile) {
      const avoidScroll = ['multiples', 'betslip', 'account'].includes(sidebarContent);
      if (!avoidScroll) {
        window.addEventListener('scroll', controlNavbar);
      } else {
        setShowLabel(true);
      }
    }
    return () => {
      window.removeEventListener('scroll', controlNavbar);
    };
  }, [sidebarContent, isMobile]);

  const isPromoPage = pathname.indexOf('/promo/') > -1;

  const navigationComponent = isPromoPage ? null : (<Navigation />);
  const footerComponent = (isPromoPage) ? null : <MobileFooter previousURL={pathname} />;
  const rightSidebarComponent = isPromoPage ? null : (
    <RightSidebar />
  );

  const isPromotion = pathname.indexOf('/promotions') === 0;
  const isEventPage = pathname.includes('/events/');

  const mainClass = classNames({
    'mb-app': true,
    'mb-app--navigation-panel-collapsed': navigationCollapsed,
    'mb-app--right-sidebar-opened': rightSidebarVisible,
    'mb-app--promoPage': isPromoPage,
  });

  const eventPageWrapperClass = classNames({
    'mb-app__eventPageWrapper': !isMobile && isEventPage && eventId && !isHomepage,
    'mb-app__exchangeMobileEventPageWrapper': isMobile && isExchange && isHomepage,
    'mb-app__mobileEventPageWrapper': isMobile && !isHomepage
  });

  return (
    <div
      className={mainClass}
      style={{
        paddingTop: bannerHeight
      }}
    >
      <ReduxBridgeProvider value={reduxBridgeData}>
        <Helmet
          titleTemplate="%s"
          defaultTitle="Matchbook.com - Sports Betting Exchange, Lowest Commissions, Best Odds Online"
        >
          <meta name="apple-itunes-app" content="app-id=503101282" />
          {!isWww && <meta name="prerender-status-code" content="301" />}
          {!isWww && (
            <meta
              name="prerender-header"
              content={`Location: https://www.matchbook.com${pathname}`}
            />
          )}
        </Helmet>
        {showNativeAppsPopup && <NativeAppsPopup onClose={handleNativeAppsModalClose} />}
        {!isPromoPage && <Header showLabel={showLabel} setShowLabel={setShowLabel} />}
        <GlobalBanner />
        {navigationComponent}
        {rightSidebarComponent}
        <MobileNavigation />
        <Modal />
        <ErrorBoundary>
          <div className="mb-app__containerChildren">
            <div className={eventPageWrapperClass}>
              {children}
              {!isMobile && !isPromotion && <Aside eventId={eventId} />}
            </div>
          </div>
        </ErrorBoundary>
        <Notification />
        {!!inlineBetslipID && isMobile && <InlineBetslip isMobile />}
        <RateLimits />
        {footerComponent}
        {!isPromoPage && (
          <div ref={footerWrapper}>
            {showFooter ? <AsyncPageFooter /> : FooterSpinner}
          </div>
        )}
        {customerOnboardingPopupVisible && (
          <CustomerOnboarding />
        )}
        <Overlay />
      </ReduxBridgeProvider>
    </div>
  );
};

App.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  windowWidthSize: PropTypes.number.isRequired,
  children: PropTypes.node,
  bannerHeight: PropTypes.number,
  navigationCollapsed: PropTypes.bool,
  navigationVisible: PropTypes.bool,
  betslipPinned: PropTypes.bool.isRequired,
  rightSidebarVisible: PropTypes.bool.isRequired,
  eventId: PropTypes.string,
  inlineBetslipID: PropTypes.string,
  setAppLoaded: PropTypes.func.isRequired,
  customerOnboardingPopupVisible: PropTypes.bool.isRequired,
  product: PropTypes.string.isRequired,
  sidebarContent: PropTypes.string.isRequired,
  uiVersion: PropTypes.string.isRequired,
};

export default App;
